import { ColumnDef } from '@tanstack/react-table';
import { memo, useCallback, useMemo } from 'react';
import { MenuTrigger } from 'react-aria-components';

import { merge } from '@/components';
import { Button, Heading, Menu, MenuItem, Popover, Table, Text } from '@/components/core';
import { AddIcon } from '@/components/icons';
import { EmailLogo, WebhookLogo } from '@/components/logos';
import { toProfileId, useGetProfile } from '@/hooks/api';
import { Notification, useValidationConfigStore } from '@/store';
import { DivProps, generateId } from '@/utils';

import { Cron } from './FrequencySetting';
import { NotificationRow } from './NotificationRow';

export type NotificationSettingsProps = DivProps;

const INotificationSettings = ({ className, ...rest }: NotificationSettingsProps) => {
  const notifications = useValidationConfigStore((state) => state.notifications);
  const addNotification = useValidationConfigStore((state) => state.addNotification);
  const { data: profile } = useGetProfile(toProfileId('me'));

  const columns = useMemo<ColumnDef<Notification>[]>(() => {
    return [
      {
        accessorKey: 'id',
        header: () => null,
        cell: ({ row }) => <NotificationRow id={row.original.id} />,
        size: Number.MAX_SAFE_INTEGER,
      },
    ];
  }, []);

  const addEmailNotification = useCallback(() => {
    if (profile) {
      addNotification({
        id: generateId(),
        type: 'email',
        emails: [profile.email],
        timezone: new Cron().timezone,
        when: new Cron().cronExpression(),
      });
    }
  }, [addNotification, profile]);

  const addHttpNotification = useCallback(() => {
    addNotification({
      id: generateId(),
      type: 'http',
      httpMethod: 'POST',
      timezone: new Cron().timezone,
      when: new Cron().cronExpression(),
    });
  }, [addNotification]);

  return (
    <div className={merge('flex flex-col gap-2 p-4', className)} {...rest}>
      <div>
        <Heading>Notification method</Heading>
        <Text>Notify members when a document is flagged for review.</Text>
      </div>

      <MenuTrigger>
        <Button className="w-fit" variant="secondary">
          <AddIcon />
          <Text>Add notification method</Text>
        </Button>
        <Popover>
          <Menu>
            <MenuItem isDisabled={!profile} onAction={addEmailNotification}>
              <EmailLogo />
              <Text>Email</Text>
            </MenuItem>
            <MenuItem onAction={addHttpNotification}>
              <WebhookLogo />
              <Text>Webhook</Text>
            </MenuItem>
          </Menu>
        </Popover>
      </MenuTrigger>

      <Table
        className="border-separate border-spacing-y-4"
        hideDragHandle
        hideHeader
        hideRowOptions
        columns={columns}
        data={notifications}
      />
    </div>
  );
};

export const NotificationSettings = memo(INotificationSettings) as typeof INotificationSettings;
